export const SUPPORT_WHATSAPP_NUMBER = "+506 7275-5629";
export const SUPPORT_TICKET_PATH = "/centro-ayuda";

const WA_BASE = "https://wa.me/50672755629";

/** Context about who is reporting the error, set once by the app. */
interface SupportContext {
  email?: string | null;
  userId?: string | null;
  storeName?: string | null;
}

let supportContext: SupportContext = {};

export function setSupportUser(ctx: Partial<SupportContext>) {
  supportContext = { ...supportContext, ...ctx };
}

export function clearSupportUser() {
  supportContext = {};
}

/** Friendly page names, matched longest-prefix first. */
const PAGE_NAMES: Record<string, string> = {
  "/": "Inicio",
  "/productos": "Productos",
  "/tiendas": "Tiendas",
  "/carrito": "Carrito",
  "/checkout": "Checkout",
  "/iniciar-sesion": "Ingreso / Registro",
  "/mis-pedidos": "Mis pedidos",
  "/favoritos": "Favoritos",
  "/mensajes": "Mensajes",
  "/centro-ayuda": "Centro de ayuda",
  "/configuracion": "Configuración de cuenta",
  "/listas-de-regalo": "Listas de regalo",
  "/vender": "Quiero vender",
  "/shop-manager": "Shop Manager",
  "/shop-manager/productos": "Shop Manager · Productos",
  "/shop-manager/pedidos": "Shop Manager · Pedidos",
  "/shop-manager/configuracion": "Shop Manager · Mi Tienda",
  "/shop-manager/importar": "Shop Manager · Importar productos",
  "/shop-manager/membresia": "Shop Manager · Membresía",
  "/shop-manager/visibilidad": "Shop Manager · Visibilidad",
  "/admin": "Admin",
};

export function getPageName(pathname: string): string {
  const match = Object.keys(PAGE_NAMES)
    .filter((p) => pathname === p || (p !== "/" && pathname.startsWith(p)))
    .sort((a, b) => b.length - a.length)[0];
  if (match) return PAGE_NAMES[match];
  if (pathname.startsWith("/tienda/")) return "Tienda de vendedor";
  if (pathname.startsWith("/producto/")) return "Detalle de producto";
  if (pathname.startsWith("/admin")) return "Admin";
  return pathname || "Sitio";
}

export interface SupportUrlOptions {
  /** e.g. "Pago", "Carga de imagen", "Autenticación" */
  errorType?: string;
}

/**
 * Builds a WhatsApp support link with a prefilled message including the page
 * name, the error type, the exact error text and the user/seller (if known).
 */
export function buildWhatsAppSupportUrl(
  errorText?: string,
  options: SupportUrlOptions = {}
): string {
  const pathname = typeof window !== "undefined" ? window.location.pathname : "";
  const pageName = pathname ? getPageName(pathname) : "";
  const { email, userId, storeName } = supportContext;

  const parts = [
    "\u{1F44B} Hola TicoMarket, necesito ayuda.",
    pageName ? `P\u00e1gina: ${pageName}${pathname ? ` (${pathname})` : ""}` : "",
    options.errorType ? `Tipo de error: ${options.errorType}` : "",
    errorText ? `Error: ${String(errorText).slice(0, 300)}` : "",
    storeName ? `Tienda: ${storeName}` : "",
    email ? `Usuario: ${email}` : "",
    userId ? `ID: ${userId}` : "",
  ].filter(Boolean);

  return `${WA_BASE}?text=${encodeURIComponent(parts.join("\n"))}`;
}

/**
 * Builds a support ticket link (/centro-ayuda) with the subject and message
 * prefilled: page name, error type, exact error and user/store when known.
 */
export function buildSupportTicketUrl(
  errorText?: string,
  options: SupportUrlOptions = {}
): string {
  const pathname = typeof window !== "undefined" ? window.location.pathname : "";
  const pageName = pathname ? getPageName(pathname) : "";
  const { email, userId, storeName } = supportContext;

  const subject = [
    options.errorType ? `Error de ${options.errorType}` : "Error en la app",
    pageName ? `en ${pageName}` : "",
  ]
    .filter(Boolean)
    .join(" ")
    .slice(0, 200);

  const body = [
    pageName ? `P\u00e1gina: ${pageName}${pathname ? ` (${pathname})` : ""}` : "",
    options.errorType ? `Tipo de error: ${options.errorType}` : "",
    errorText ? `Error exacto: ${String(errorText).slice(0, 500)}` : "",
    storeName ? `Tienda: ${storeName}` : "",
    email ? `Usuario: ${email}` : "",
    userId ? `ID: ${userId}` : "",
    "",
    "\u00bfQu\u00e9 estabas haciendo cuando ocurri\u00f3?:",
  ]
    .filter(Boolean)
    .join("\n")
    .slice(0, 2000);

  const params = new URLSearchParams({ subject, message: body });
  return `${SUPPORT_TICKET_PATH}?${params.toString()}`;
}
